/**
 * biometricStore.js — Face ID / Touch ID App-Lock für native iOS
 *
 * Funktionsweise:
 *   1. Beim App-Start: wenn JWT vorhanden + Biometrie aktiviert → Lock-Overlay zeigen
 *   2. Nach X Minuten im Hintergrund → beim Zurückkehren wieder sperren
 *   3. Erfolgreiche Authentifizierung → Overlay ausblenden, App nutzbar
 *   4. Fehlschlag / Abbruch → Token löschen → Login-Seite
 *
 * Nur wirksam wenn isNative === true. Im Browser keine Auswirkung.
 *
 * Abhängigkeit: @aparajita/capacitor-biometric-auth
 * Gesetzt von: App.vue (onMounted + visibilitychange / App.addListener('resume'))
 */

import { isNative } from '@/services/platform'

const PREF_KEY = 'biometricEnabled'
// Sekunden im Hintergrund bevor bei Rückkehr neu gesperrt wird
const LOCK_AFTER_SECONDS = 60

// ─── In-Memory-Cache ──────────────────────────────────────────────────────────
let _enabled = null         // null = noch nicht gelesen
let _available = null       // null = noch nicht geprüft
let _lastBackground = null  // Zeitstempel (ms) wann App in Hintergrund ging

// ─── Lazy-Imports ─────────────────────────────────────────────────────────────
let _bioPlugin = null
let _prefsPlugin = null

async function getBioPlugin() {
  if (_bioPlugin) return _bioPlugin
  const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth')
  _bioPlugin = BiometricAuth
  return _bioPlugin
}

async function getPrefs() {
  if (_prefsPlugin) return _prefsPlugin
  const { Preferences } = await import('@capacitor/preferences')
  _prefsPlugin = Preferences
  return _prefsPlugin
}

// ─── Public API ───────────────────────────────────────────────────────────────
export const biometricStore = {
  /**
   * Prüft ob Biometrie auf dem Gerät verfügbar und nutzbar ist.
   * Ergebnis wird gecacht — kein erneuter Bridge-Call bei jedem Aufruf.
   * @returns {Promise<boolean>}
   */
  async isAvailable() {
    if (!isNative) return false
    if (_available !== null) return _available

    try {
      const bio = await getBioPlugin()
      const result = await bio.checkBiometry()
      // isAvailable = true wenn Biometrie eingerichtet und entsperrt
      _available = result.isAvailable === true
      console.info(
        `[biometricStore] checkBiometry → isAvailable=${result.isAvailable}`,
        `biometryType=${result.biometryType}`,
        `strongBiometryIsAvailable=${result.strongBiometryIsAvailable}`,
        result.code ? `code=${result.code}` : '',
      )
    } catch (err) {
      // Häufige Ursache: Plugin-Swift-Code nicht kompiliert (Xcode Clean Build nötig)
      // oder Gerät ohne Biometrie / nicht eingerichtet.
      console.warn('[biometricStore] checkBiometry fehlgeschlagen:', err?.message ?? err)
      _available = false
    }
    return _available
  },

  /**
   * Gibt zurück ob der User Biometrie in den Einstellungen aktiviert hat.
   * Liest beim ersten Aufruf aus Preferences, danach gecacht.
   * @returns {Promise<boolean>}
   */
  async isEnabled() {
    if (!isNative) return false
    if (_enabled !== null) return _enabled

    try {
      const Prefs = await getPrefs()
      const { value } = await Prefs.get({ key: PREF_KEY })
      _enabled = value === 'true'
    } catch {
      _enabled = false
    }
    return _enabled
  },

  /**
   * Synchroner Lesezugriff auf den gecachten Wert (nach init()).
   * Vor init() immer false.
   * @returns {boolean}
   */
  isEnabledSync() {
    return _enabled === true
  },

  /**
   * Aktiviert oder deaktiviert den Biometrie-Lock.
   * Persistiert in Preferences, aktualisiert Cache synchron.
   * @param {boolean} value
   */
  async setEnabled(value) {
    _enabled = Boolean(value)

    try {
      const Prefs = await getPrefs()
      if (_enabled) {
        await Prefs.set({ key: PREF_KEY, value: 'true' })
      } else {
        await Prefs.remove({ key: PREF_KEY })
      }
    } catch (err) {
      console.warn('[biometricStore] setEnabled failed:', err)
    }
  },

  /**
   * Initialisiert den Cache (isEnabled + isAvailable).
   * Sollte früh beim App-Start aufgerufen werden.
   */
  async init() {
    await this.isAvailable()
    await this.isEnabled()
  },

  /**
   * Löst den Biometrie-Dialog aus.
   * @param {string} reason — Text der dem User im Dialog angezeigt wird
   * @returns {Promise<boolean>} — true = Erfolg, false = Fehlschlag/Abbruch
   */
  async authenticate(reason) {
    if (!isNative) return true

    try {
      const bio = await getBioPlugin()
      await bio.authenticate({ reason })
      console.info('[biometricStore] authenticate erfolgreich')
      return true
    } catch (err) {
      // BiometryErrorType.userCancel / userFallback → normaler Abbruch, kein Problem
      // BiometryErrorType.biometryNotAvailable / notEnrolled → Plugin-/Geräteproblem
      console.warn('[biometricStore] authenticate fehlgeschlagen:', err?.code, err?.message)
      return false
    }
  },

  /**
   * Muss aufgerufen werden wenn die App in den Hintergrund geht.
   * Speichert den Zeitstempel für die Inaktivitätsprüfung.
   */
  markBackground() {
    _lastBackground = Date.now()
  },

  /**
   * Gibt an ob beim Zurückkehren in den Vordergrund gesperrt werden soll.
   * true wenn Biometrie aktiviert + verfügbar + im Hintergrund länger als LOCK_AFTER_SECONDS.
   * @returns {boolean}
   */
  shouldLockOnResume() {
    if (!isNative || !_enabled || !_available) return false
    if (_lastBackground === null) return false
    const elapsed = (Date.now() - _lastBackground) / 1000
    return elapsed >= LOCK_AFTER_SECONDS
  },

  /**
   * Gibt den Biometrie-Typ als lesbaren String zurück (für UI-Labels).
   * @returns {Promise<string>} — 'Face ID' | 'Touch ID' | 'Biometrie'
   */
  async getBiometryLabel() {
    if (!isNative) return 'Biometrie'
    try {
      const bio = await getBioPlugin()
      const { biometryType } = await bio.checkBiometry()
      // BiometryType: faceId = 2, touchId = 1
      if (biometryType === 2) return 'Face ID'
      if (biometryType === 1) return 'Touch ID'
    } catch { /* ignore */ }
    return 'Biometrie'
  },
}
